import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Slider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import axios from 'axios';
import { AuthContext } from '../../AuthContext';

const MovementZones = ({ open, onClose, zoneType = 'movement' }) => {
  const [loading, setLoading] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [hasSnapshots, setHasSnapshots] = useState(false);
  const [latestSnapshot, setLatestSnapshot] = useState(null);
  const [currentSnapshot, setCurrentSnapshot] = useState(null); // The snapshot being used for zones
  const [isPolling, setIsPolling] = useState(false);
  const [hasNewerSnapshot, setHasNewerSnapshot] = useState(false);
  const [isPainting, setIsPainting] = useState(false);
  const [currentTool, setCurrentTool] = useState('paint'); // 'paint' or 'erase'
  const [brushSize, setBrushSize] = useState(75);
  const [showMask, setShowMask] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingZones, setLoadingZones] = useState(false);
  const [canvasSetupState, setCanvasSetupState] = useState('idle'); // 'idle', 'loading', 'success', 'failed'
  const { globalTenantId } = useContext(AuthContext);
  const pollingIntervalRef = useRef(null);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const isDrawing = useRef(false);
  const strokeHistory = useRef([]);
  const currentStroke = useRef(null);
  const preservedCanvasData = useRef(null); // To preserve canvas when switching snapshots
  const hasAutoSelectedRef = useRef(false); // Track if we've already auto-selected a snapshot

  // Define zone-specific configurations
  const zoneConfig = {
    movement: {
      title: 'Movement Zones Configuration',
      description: 'Paint over areas you want to exclude from movement detection (e.g., trees, flags, busy roads).',
      apiEndpoint: `/api/tenants/${globalTenantId}/movement-zones`,
      settingsProperty: 'movementZones'
    },
    anpr: {
      title: 'ANPR Movement Zones Configuration', 
      description: 'Paint over areas you want to exclude from ANPR detection (e.g., areas where number plates should not be scanned).',
      apiEndpoint: `/api/tenants/${globalTenantId}/anpr-movement-zones`,
      settingsProperty: 'anprMovementZones'
    }
  };

  const currentConfig = zoneConfig[zoneType];

  // Fetch snapshots when dialog opens
  useEffect(() => {
    if (open && globalTenantId) {
      fetchSnapshots();
    }
  }, [open, globalTenantId]);

  // Manage hasNewerSnapshot state based on current and latest snapshots
  useEffect(() => {
    if (currentSnapshot && latestSnapshot) {
      const shouldShowNewer = latestSnapshot.id > currentSnapshot.id;
      
      if (shouldShowNewer !== hasNewerSnapshot) {
        setHasNewerSnapshot(shouldShowNewer);
      }
    } else if (hasNewerSnapshot) {
      // Reset if we don't have both snapshots
      setHasNewerSnapshot(false);
    }
  }, [currentSnapshot, latestSnapshot]);

  // Set up continuous polling when dialog is open
  useEffect(() => {
    if (open && globalTenantId) {
      setIsPolling(true);
      pollingIntervalRef.current = setInterval(() => {
        fetchSnapshots(true); // Silent fetch (no loading spinner)
      }, 3000); // Check every 3 seconds
    } else {
      setIsPolling(false);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }

    // Cleanup on unmount or when dialog closes
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [open, globalTenantId]);

  // Set up canvas when currentSnapshot changes
  useEffect(() => {
    if (currentSnapshot && currentSnapshot.CloudFrontUrl && canvasRef.current && imageRef.current) {
      const canvas = canvasRef.current;
      
      // Check if canvas already has content that we should preserve
      let hasExistingContent = false;
      if (canvas.width > 0 && canvas.height > 0) {
        try {
          const ctx = canvas.getContext('2d');
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          hasExistingContent = imageData.data.some((value, index) => {
            return (index + 1) % 4 === 0 && value > 0; // Check alpha channel
          });
        } catch (error) {
          console.warn('Could not check existing canvas content:', error);
        }
      }
      
      // Determine if we should preserve content
      const shouldPreserve = hasExistingContent || preservedCanvasData.current !== null;
      
      console.log('Canvas setup decision:', {
        hasExistingContent,
        hasPreservedData: preservedCanvasData.current !== null,
        shouldPreserve
      });
      
      // Setup canvas and then load zones if successful
      setCanvasSetupState('loading');
      setupCanvasWithImageCheck(shouldPreserve)
        .then((success) => {
          if (success) {
            setCanvasSetupState('success');
            // Only load movement zones if canvas setup was successful and we're not preserving content
            if (!shouldPreserve) {
              console.log('Canvas setup successful, loading zones...');
              loadMovementZones();
            }
          } else {
            console.warn('Canvas setup failed, skipping zone load');
            setCanvasSetupState('failed');
          }
        })
        .catch((error) => {
          console.error('Canvas setup failed:', error);
          setCanvasSetupState('failed');
        });
    }
  }, [currentSnapshot]);

  const fetchSnapshots = async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    
    try {
      // Build query parameters based on zone type
      const params = {
        page: 0,
        pageSize: 1,
        sortColumn: 'id',
        sortDirection: 'desc'
      };
      
      // Add filter based on zone type
      if (zoneType === 'anpr') {
        params.anprOnly = 'true';
      } else if (zoneType === 'movement') {
        params.movementOnly = 'true';
      }
      
      const response = await axios.get(`/api/kiosk-snapshots?tenantId=${globalTenantId}`, { params });
      
      const data = response.data;
      setSnapshots(data.rows || []);
      const hasSnapshotsNow = data.count > 0;
      const hadSnapshotsBefore = hasSnapshots; // Use current state value
      setHasSnapshots(hasSnapshotsNow);
      
      if (data.rows && data.rows.length > 0) {
        const newest = data.rows[0];
        setLatestSnapshot(newest);
        
        // Auto-select ONLY once when transitioning from no snapshots to having snapshots
        // Use ref to ensure this only happens once per dialog session
        const shouldAutoSelect = !hasAutoSelectedRef.current && !hadSnapshotsBefore && hasSnapshotsNow && !currentSnapshot;
        
        console.log('fetchSnapshots: Auto-selection decision:', {
          hasAutoSelectedRef: hasAutoSelectedRef.current,
          hadSnapshotsBefore,
          hasSnapshotsNow,
          currentSnapshot: !!currentSnapshot,
          silent,
          shouldAutoSelect,
          zoneType
        });
        
        if (shouldAutoSelect) {
          hasAutoSelectedRef.current = true; // Mark that we've auto-selected
          preservedCanvasData.current = null; // Clear any preserved data for auto-selection
          console.log('fetchSnapshots: Auto-selecting snapshot', newest.id);
          setCurrentSnapshot(newest);
        }
      }
    } catch (error) {
      console.error('Error fetching snapshots:', error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const setupCanvasWithImageCheck = async (preserveContent = false) => {
    const image = imageRef.current;
    const canvas = canvasRef.current;
    
    if (!image || !canvas) {
      console.warn('Canvas setup: Missing image or canvas ref');
      return false;
    }

    console.log('Canvas setup: Starting setup for snapshot', currentSnapshot?.id, 'preserve:', preserveContent);

    try {
      // Check if image is already loaded
      if (image.complete && image.naturalWidth > 0) {
        console.log('Canvas setup: Image already loaded, proceeding immediately');
        setupCanvas(preserveContent);
        return true;
      }

      console.log('Canvas setup: Waiting for image to load...');

      // Wait for image to load with better error handling
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.error('Canvas setup: Image load timeout after 15 seconds');
          // Clean up event listeners before rejecting
          image.onload = null;
          image.onerror = null;
          reject(new Error('Image load timeout'));
        }, 15000); // Increased timeout to 15 seconds

        const cleanup = () => {
          clearTimeout(timeout);
          image.onload = null;
          image.onerror = null;
        };

        image.onload = () => {
          console.log('Canvas setup: Image loaded successfully');
          cleanup();
          resolve();
        };

        image.onerror = (error) => {
          console.error('Canvas setup: Image failed to load:', error);
          cleanup();
          reject(new Error('Image failed to load'));
        };

        // Double-check if image loaded while setting up listeners
        if (image.complete && image.naturalWidth > 0) {
          console.log('Canvas setup: Image was already loaded during listener setup');
          cleanup();
          resolve();
        }
      });

      // Additional HTTP check to ensure image is accessible
      try {
        const response = await fetch(currentSnapshot.CloudFrontUrl, { method: 'HEAD' });
        if (!response.ok) {
          console.error('Canvas setup: Image not accessible via HTTP:', response.status);
          throw new Error(`Image not accessible: ${response.status}`);
        }
        console.log('Canvas setup: Image accessibility confirmed');
      } catch (fetchError) {
        console.error('Canvas setup: Failed to verify image accessibility:', fetchError);
        // Don't fail completely on fetch error - the image might still work
        console.warn('Canvas setup: Continuing despite fetch error...');
      }

      // Final check before proceeding to canvas setup
      if (!image.complete || image.naturalWidth === 0) {
        console.error('Canvas setup: Image still not ready after all checks');
        return false;
      }

      setupCanvas(preserveContent);
      console.log('Canvas setup: Completed successfully');
      return true;

    } catch (error) {
      console.error('Canvas setup: Error during image loading:', error);
      
      // Try to recover by attempting setup anyway if image seems ready
      if (image.complete && image.naturalWidth > 0) {
        console.log('Canvas setup: Attempting recovery with available image data...');
        try {
          setupCanvas(preserveContent);
          console.log('Canvas setup: Recovery successful');
          return true;
        } catch (recoveryError) {
          console.error('Canvas setup: Recovery failed:', recoveryError);
        }
      }
      
      return false;
    }
  };

  const setupCanvas = (preserveContent = false) => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image) {
      console.warn('setupCanvas: Missing canvas or image ref');
      return;
    }

    const ctx = canvas.getContext('2d');

    // Double-check image is ready
    if (!image.complete || image.naturalWidth === 0) {
      console.warn('setupCanvas: Image not ready for canvas setup');
      return;
    }

    console.log('setupCanvas: Setting up canvas with dimensions', image.naturalWidth, 'x', image.naturalHeight);

    // Save existing canvas content if preserving, or use previously preserved data
    let existingDataURL = null;
    if (preserveContent) {
      if (preservedCanvasData.current) {
        existingDataURL = preservedCanvasData.current;
        console.log('setupCanvas: Using previously preserved canvas data');
        preservedCanvasData.current = null; // Clear after using
      } else if (canvas.width > 0 && canvas.height > 0) {
        try {
          existingDataURL = canvas.toDataURL('image/png');
          console.log('setupCanvas: Preserved existing canvas content as data URL');
        } catch (error) {
          console.warn('setupCanvas: Could not preserve canvas content:', error);
        }
      }
    }

    // Set canvas size to match image
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    
    // Set display size to match image display size
    const rect = image.getBoundingClientRect();
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';

    console.log('setupCanvas: Canvas display size set to', rect.width, 'x', rect.height);

    // Clear canvas with transparent background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set up drawing style for transparency support
    ctx.globalCompositeOperation = 'source-over';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalAlpha = 1.0; // Ensure global alpha is at full opacity for individual stroke transparency to work

    // Restore preserved content or redraw strokes
    if (existingDataURL && preserveContent) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        console.log('setupCanvas: Restored preserved canvas content');
      };
      img.onerror = () => {
        console.warn('setupCanvas: Failed to restore preserved content');
        redrawStrokes();
      };
      img.src = existingDataURL;
    } else {
      // Redraw all existing strokes (don't clear stroke history)
      console.log('setupCanvas: Redrawing', strokeHistory.current.length, 'strokes');
      redrawStrokes();
    }
  };

  const redrawStrokes = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas first
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
        // Redraw all strokes from history
    strokeHistory.current.forEach(stroke => {
      ctx.lineWidth = stroke.brushSize;
      
      // Set composite operation and color based on stroke type
      if (stroke.tool === 'erase') {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = 'rgb(0, 0, 0)';
        ctx.globalAlpha = 1.0; // Full opacity for erasing
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = 'rgb(255, 0, 0)';
        ctx.globalAlpha = 1.0; // Solid paint - transparency handled by CSS
      }
      
      ctx.beginPath();
      
      stroke.points.forEach((point, index) => {
        if (index === 0) {
          ctx.moveTo(point.x, point.y);
        } else {
          ctx.lineTo(point.x, point.y);
        }
      });
      
      ctx.stroke();
    });
    
    // Reset to default composite operation and alpha
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1.0;
  };

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (!isPainting) return;
    
    isDrawing.current = true;
    const coords = getCanvasCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    
    // Start new stroke
    currentStroke.current = {
      points: [coords],
      brushSize: brushSize,
      color: currentTool === 'paint' ? 'rgb(255, 0, 0)' : 'rgb(0, 0, 0)', // Solid colors - transparency handled by CSS
      tool: currentTool
    };
    
    // Set composite operation based on current tool
    if (currentTool === 'erase') {
      ctx.globalCompositeOperation = 'destination-out';
    } else {
      ctx.globalCompositeOperation = 'source-over';
    }
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    if (!isPainting || !isDrawing.current) return;
    
    const coords = getCanvasCoordinates(e);
    const ctx = canvasRef.current.getContext('2d');
    
    // Add point to current stroke
    if (currentStroke.current) {
      currentStroke.current.points.push(coords);
    }
    
    ctx.lineWidth = brushSize;
    
    if (currentTool === 'paint') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = 'rgb(255, 0, 0)'; // Solid red - transparency handled by CSS
      ctx.globalAlpha = 1.0; // Full opacity for individual strokes
    } else {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.globalAlpha = 1.0; // Full opacity for erasing
    }
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing.current && currentStroke.current) {
      // Save the completed stroke to history
      strokeHistory.current.push(currentStroke.current);
      currentStroke.current = null;
    }
    isDrawing.current = false;
    
    // Reset composite operation and alpha
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1.0;
    }
  };

  const clearMask = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Reset composite operation to default
      ctx.globalCompositeOperation = 'source-over';
    }
    // Clear session history for undo functionality
    strokeHistory.current = [];
    currentStroke.current = null;
  };

  const undoLastStroke = () => {
    if (strokeHistory.current.length === 0) return;
    
    // Remove the last stroke from history
    strokeHistory.current.pop();
    
    // Clear canvas and redraw all remaining strokes
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Redraw all strokes except the last one
    strokeHistory.current.forEach(stroke => {
      if (stroke.points.length > 0) {
        ctx.lineWidth = stroke.brushSize;
        
        // Set composite operation and color based on stroke type
        if (stroke.tool === 'erase') {
          ctx.globalCompositeOperation = 'destination-out';
          ctx.strokeStyle = 'rgb(0, 0, 0)';
          ctx.globalAlpha = 1.0; // Full opacity for erasing
        } else {
          ctx.globalCompositeOperation = 'source-over';
          ctx.strokeStyle = 'rgb(255, 0, 0)';
          ctx.globalAlpha = 1.0; // Solid paint - transparency handled by CSS
        }
        
        ctx.beginPath();
        ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
        
        for (let i = 1; i < stroke.points.length; i++) {
          ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        }
        
        ctx.stroke();
      }
    });
    
    // Reset composite operation and alpha
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1.0;
  };

  const handleRefresh = () => {
    fetchSnapshots();
  };

  const handleUseLatestSnapshot = () => {
    // Save current canvas state before switching snapshots
    const canvas = canvasRef.current;
    if (canvas && canvas.width > 0 && canvas.height > 0) {
      try {
        // Check if canvas has any content
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hasContent = imageData.data.some((value, index) => {
          return (index + 1) % 4 === 0 && value > 0; // Check alpha channel
        });
        
        if (hasContent) {
          preservedCanvasData.current = canvas.toDataURL('image/png');
          console.log('Preserved canvas state before snapshot change');
        }
      } catch (error) {
        console.warn('Could not preserve canvas state:', error);
      }
    }
    
    setCanvasSetupState('idle');
    setCurrentSnapshot(latestSnapshot);
  };

  const retryCanvasSetup = () => {
    if (currentSnapshot && currentSnapshot.CloudFrontUrl && canvasRef.current && imageRef.current) {
      console.log('Retrying canvas setup...');
      setCanvasSetupState('loading');
      
      // Force a fresh image load by updating the src
      const image = imageRef.current;
      const originalSrc = image.src;
      image.src = '';
      
      setTimeout(() => {
        image.src = originalSrc + '?retry=' + Date.now(); // Add cache-busting parameter
        
        setupCanvasWithImageCheck(false)
          .then((success) => {
            if (success) {
              setCanvasSetupState('success');
              console.log('Retry successful, loading zones...');
              loadMovementZones();
            } else {
              setCanvasSetupState('failed');
            }
          })
          .catch((error) => {
            console.error('Retry failed:', error);
            setCanvasSetupState('failed');
          });
      }, 100);
    }
  };

  const loadMovementZones = async () => {
    if (!globalTenantId) {
      console.warn('loadMovementZones: No globalTenantId available');
      return;
    }
    
    // Verify canvas is ready before proceeding
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      console.warn('loadMovementZones: Canvas not ready, skipping zone load');
      return;
    }
    
    setLoadingZones(true);
    try {
      console.log('loadMovementZones: Fetching zones from server...');
      const response = await axios.get(currentConfig.apiEndpoint);
      const zones = response.data[currentConfig.settingsProperty];
      
      if (zones) {
        console.log('loadMovementZones: Loading saved zone data...');
        
        // Load the saved movement zone image data with proper error handling
        await new Promise((resolve, reject) => {
          const ctx = canvas.getContext('2d');
          
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          
          const img = new Image();
          
          const timeout = setTimeout(() => {
            console.error('loadMovementZones: Timeout loading zones image');
            img.onload = null;
            img.onerror = null;
            reject(new Error('Zones image load timeout'));
          }, 10000);
          
          img.onload = () => {
            try {
              clearTimeout(timeout);
              console.log('loadMovementZones: Zones image loaded, drawing to canvas...');
              
              // Verify canvas is still valid
              if (canvas.width === 0 || canvas.height === 0) {
                throw new Error('Canvas dimensions invalid when drawing zones');
              }
              
              // Clear canvas first
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              
              // Draw the loaded mask
              ctx.drawImage(img, 0, 0);
              
              console.log(`${zoneType} zones loaded and drawn successfully`);
              resolve();
            } catch (drawError) {
              console.error('loadMovementZones: Error drawing zones to canvas:', drawError);
              reject(drawError);
            }
          };
          
          img.onerror = (error) => {
            clearTimeout(timeout);
            console.error('loadMovementZones: Failed to load zones image:', error);
            reject(new Error('Failed to load zones image data'));
          };
          
          img.src = zones;
        });
        
      } else {
        console.log(`loadMovementZones: No saved ${zoneType} zones found`);
      }
    } catch (error) {
      console.error(`Error loading ${zoneType} zones:`, error);
      // Don't leave the user in a broken state - they can still paint new zones
    } finally {
      setLoadingZones(false);
    }
  };

  const saveMovementZones = async () => {
    if (!globalTenantId) return;
    
    setSaving(true);
    try {
      const canvas = canvasRef.current;
      let zones = null;
      
      if (canvas) {
        // Check if canvas has any content (not just transparent)
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hasContent = imageData.data.some((value, index) => {
          // Check alpha channel (every 4th value) for non-zero values
          return (index + 1) % 4 === 0 && value > 0;
        });
        
        if (hasContent) {
          // Convert canvas to PNG data URL (only the final mask state)
          zones = canvas.toDataURL('image/png');
          console.log(`${zoneType} zones saved as image data, size:`, zones.length, 'starts with:', zones.substring(0, 50));
        } else {
          console.log(`No ${zoneType} zones to save (canvas is empty)`);
        }
      }
      
      await axios.put(currentConfig.apiEndpoint, {
        [currentConfig.settingsProperty]: zones
      });
      
      console.log(`${zoneType} zones saved successfully`);
      
      // Request kiosk refresh (same as in Settings.js)
      axios.post(`/api/refreshKioskUI`, {tenantId: globalTenantId, room: 'Room'}).then(result => {
        console.log('Kiosk UI refresh request sent');
      }).catch(error => { 
        console.error('Error sending Kiosk UI refresh request:', error);
      });
      
    } catch (error) {
      console.error(`Error saving ${zoneType} zones:`, error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
      throw error; // Re-throw so calling component can handle
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // Stop polling when dialog closes
    setIsPolling(false);
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    // Reset state
    setCurrentSnapshot(null);
    setLatestSnapshot(null);
    setSnapshots([]);
    setHasSnapshots(false);
    setHasNewerSnapshot(false);
    setIsPainting(false);
    setCurrentTool('paint');
    setCanvasSetupState('idle');
    // Clear session-only stroke history (not needed for persistence)
    strokeHistory.current = [];
    currentStroke.current = null;
    preservedCanvasData.current = null; // Clear preserved canvas data
    hasAutoSelectedRef.current = false; // Reset auto-selection flag for next time
    onClose();
  };

  const handleSaveZones = async () => {
    try {
      await saveMovementZones();
      handleClose();
    } catch (error) {
      // Error is already logged in saveMovementZones
      // Could add user-facing error notification here if needed
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        sx: {
          maxWidth: '95vw',
          width: 'auto'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {currentConfig.title}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : !hasSnapshots ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>No Snapshots Available</AlertTitle>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {zoneType === 'anpr' ? 'ANPR zones' : 'Movement zones'} require at least one {zoneType === 'anpr' ? 'ANPR' : 'movement'} snapshot to configure detection areas. 
              To get started, please trigger the {zoneType === 'anpr' ? 'ANPR system' : 'movement detection system'}:
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Step 1:</strong> {zoneType === 'anpr' ? 'Drive a vehicle in front of the kiosk camera' : 'Wave your hand in front of the kiosk camera for a few seconds'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Step 2:</strong> Wait for the {zoneType === 'anpr' ? 'ANPR system' : 'movement detection'} to capture a snapshot
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: isPolling ? 'success.main' : 'text.secondary' }}>
              <strong>Step 3:</strong> {isPolling ? 'Automatically checking for new snapshots...' : 'Click "Refresh" to check for new snapshots'}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button 
                variant="contained" 
                onClick={handleRefresh}
                disabled={isPolling}
              >
                {isPolling ? 'Auto-checking...' : 'Refresh'}
              </Button>
              {isPolling && <CircularProgress size={20} />}
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Note: {zoneType === 'anpr' ? 'ANPR' : 'Movement detection'} must be enabled in the settings for this to work.
              {isPolling && ' We\'re automatically checking for new snapshots every 3 seconds.'}
            </Typography>
          </Alert>
        ) : (
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {currentConfig.description}
            </Typography>

            {/* Show loading state when snapshots exist but currentSnapshot is being set up */}
            {!currentSnapshot && latestSnapshot && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">
                    Setting up snapshot for zone configuration...
                  </Typography>
                </Box>
              </Alert>
            )}

            {currentSnapshot && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">
                    Snapshot captured {new Date(currentSnapshot.createdAt).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: '2-digit', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                    {hasNewerSnapshot && (
                      <Typography 
                        component="span" 
                        sx={{ 
                          color: 'primary.main', 
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          ml: 1,
                          '&:hover': {
                            color: 'primary.dark'
                          }
                        }}
                        onClick={handleUseLatestSnapshot}
                      >
                        (newer available)
                      </Typography>
                    )}
                  </Typography>
                </Box>

                {/* Main layout with image on left and controls on right */}
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  {/* Image and Canvas */}
                  <Box sx={{ flex: '0 0 auto', maxWidth: '600px' }}>
                    {currentSnapshot.CloudFrontUrl && (
                      <Box>
                        {/* Canvas Setup Status */}
                        {canvasSetupState === 'loading' && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CircularProgress size={16} />
                              <Typography variant="body2">
                                Loading image and setting up canvas...
                              </Typography>
                            </Box>
                          </Alert>
                        )}
                        
                        {canvasSetupState === 'failed' && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            <AlertTitle>Canvas Setup Failed</AlertTitle>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              The image failed to load properly. This might be due to a slow connection or temporary server issue.
                            </Typography>
                            <Button 
                              variant="outlined" 
                              size="small" 
                              onClick={retryCanvasSetup}
                            >
                              Retry Setup
                            </Button>
                          </Alert>
                        )}
                        
                        <Box sx={{ 
                          border: '1px solid #ddd', 
                          borderRadius: 1, 
                          p: 1,
                          backgroundColor: '#f9f9f9',
                          textAlign: 'center',
                          position: 'relative',
                          display: 'inline-block',
                          opacity: canvasSetupState === 'loading' ? 0.6 : 1
                        }}>
                          <img 
                            ref={imageRef}
                            src={currentSnapshot.CloudFrontUrl} 
                            alt="Current snapshot for zones" 
                            style={{ 
                              maxWidth: '100%', 
                              height: 'auto',
                              maxHeight: '450px',
                              display: 'block'
                            }}
                          />
                          <canvas
                            ref={canvasRef}
                            style={{
                              position: 'absolute',
                              top: '8px',
                              left: '8px',
                              cursor: (isPainting && canvasSetupState === 'success') ? (currentTool === 'paint' ? 'crosshair' : 'grab') : 'default',
                              pointerEvents: (isPainting && canvasSetupState === 'success') ? 'auto' : 'none',
                              opacity: showMask ? 0.4 : 0 // Semi-transparent canvas overlay
                            }}
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {/* Painting Controls */}
                  <Box sx={{ 
                    width: 300, 
                    flex: '0 0 300px',
                    p: 2, 
                    border: '1px solid #ddd', 
                    borderRadius: 1, 
                    backgroundColor: '#f0f8ff',
                    height: 'fit-content'
                  }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Mask Controls
                      {loadingZones && (
                        <CircularProgress size={16} sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isPainting}
                          onChange={(e) => setIsPainting(e.target.checked)}
                          color="primary"
                          disabled={canvasSetupState !== 'success'}
                        />
                      }
                      label={canvasSetupState === 'success' ? "Paint Mode" : "Paint Mode (Canvas Loading...)"}
                      sx={{ mb: 2, display: 'block' }}
                    />
                    
                    {/* Tool Selection */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        Tool:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant={currentTool === 'paint' ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => setCurrentTool('paint')}
                          disabled={!isPainting || canvasSetupState !== 'success'}
                          sx={{ flex: 1 }}
                        >
                          🎨 Paint
                        </Button>
                        <Button
                          variant={currentTool === 'erase' ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => setCurrentTool('erase')}
                          disabled={!isPainting || canvasSetupState !== 'success'}
                          sx={{ flex: 1 }}
                        >
                          🧽 Erase
                        </Button>
                      </Box>
                    </Box>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" gutterBottom>
                        Brush Size:
                      </Typography>
                      <Box sx={{ px: 2 }}>
                        <Slider
                          value={brushSize}
                          onChange={(e, value) => setBrushSize(value)}
                          min={50}
                          max={500}
                          disabled={!isPainting || canvasSetupState !== 'success'}
                          marks={[
                            { value: 50, label: 'Small' },
                            { value: 150, label: 'Med' },
                            { value: 300, label: 'Large' },
                            { value: 500, label: 'XL' }
                          ]}
                          sx={{ mt: 1 }}
                        />
                      </Box>
                    </Box>

                    <Button 
                      variant="outlined" 
                      fullWidth
                      onClick={clearMask}
                      disabled={!isPainting || canvasSetupState !== 'success'}
                      sx={{ mb: 2 }}
                    >
                      Clear Masks
                    </Button>
                    
                    <Button 
                      variant="outlined" 
                      fullWidth
                      onClick={undoLastStroke}
                      disabled={!isPainting || canvasSetupState !== 'success' || strokeHistory.current.length === 0}
                      sx={{ mb: 2 }}
                    >
                      Undo Last Stroke
                    </Button>
                    
                    <Typography variant="caption" color="text.secondary" display="block">
                      💡 Use Paint to mask areas and Erase to remove masks. Enable paint mode and drag to edit detection areas.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Close</Button>
        {currentSnapshot && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleSaveZones}
            disabled={saving}
          >
            {saving ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              'Save Zones'
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MovementZones; 